import {
  CancelEventDiffDetailsInput,
  ConferencingType,
  EventPermissions,
  RepeatingEventSaveOption,
  ResponseStatusEnum,
  SaveModifyEventDiffDetailsInput,
} from "@clockwise/schema";
import { useEcosystem } from "@clockwise/web-commons/src/util/ecosystem";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import { isEqual, noop } from "lodash";
import { Interval } from "luxon";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useUserConfrencingTypes } from "../../../../../../hooks/useUserConfrencingTypes";
import { useWorkingHours } from "../../../../../account-preferences-working-meeting-hours/hooks/useWorkingHours";
import { EventCardExpanded } from "../../../../../event-card/EventCardExpanded";
import { Divider } from "../../../../../event-card/atoms/Divider";
import { TimeTradeoffs } from "../../../../../event-card/atoms/TimeTradeoffs";
import { WarningMessage } from "../../../../../event-card/atoms/WarningMessage";
import { useEventAttendees } from "../../../../../event-card/hooks/EventAttendeesContext";
import { useReadEventTimeDetails } from "../../../../../event-card/hooks/EventCardContext";
import { ECAgenda } from "../../../../../event-card/molecules/ECAgenda";
import { ECAttendeeWrapper } from "../../../../../event-card/molecules/ECAttendeesV2/ECAttendeeWrapper";
import { ECFlexSettings } from "../../../../../event-card/molecules/ECFlexSettings";
import { ECFlexibility } from "../../../../../event-card/molecules/ECFlexibility";
import { ECFooter } from "../../../../../event-card/molecules/ECFooter";
import {
  Recurrence,
  SelectableRecurrenceOption,
} from "../../../../../event-card/molecules/ECRecurrence";
import { ECResponseStatus } from "../../../../../event-card/molecules/ECResponseStatus";
import { ECRoomAndLocation } from "../../../../../event-card/molecules/ECRoomAndLocation";
import { ECTitle } from "../../../../../event-card/molecules/ECTitle";
import { ECVideoConf } from "../../../../../event-card/molecules/ECVideoConf";
import {
  ALL_WORK_DAYS_SELECTED,
  EMPTY_FLEX_DETAILS,
  EventCardFlex,
  EventCardMode,
} from "../../../../../event-card/types";
import { getEventCardWarning } from "../../../../../event-card/utils/getEventCardWarning";
import { getEventMinMaxConstraints } from "../../../../../event-card/utils/getEventMinMaxConstraints";
import { getListOfNonRemovableAttendeeIds } from "../../../../../event-card/utils/getListOfNonRemovableAttendeeIds";
import {
  getDeleteEventCopy,
  getSubmitButtonCopy,
} from "../../../../../event-card/utils/getSubmitButtonCopy";
import {
  canModifyWholeEvent,
  canUserInviteAttendees,
  canUserRSVP,
  isWaitingOnAsync,
} from "../../../../../event-card/utils/getUserEventPermissions.util";
import useOrgDomains from "../../../../../hooks/useOrgDomains";
import {
  convertStringToDayOfWeek,
  formatRecurrenceToString,
  formatSparkleInTitle,
  getDisabledSaveOptions,
} from "../../../../../web-app-calendar/hooks/useEditExistingEvent.util";
import { useReadDefaultExpandedTradeoff } from "../../../hooks/ExpandedTradeoffContext";
import { useResetEditsToAttendees as useResetEditsToAttendeesForMultiCalendar } from "../../../hooks/multicalendar/event-card-edit/useResetEditsToAttendees";
import { useSetOriginalAttendeesBeforeEdit as useSetOriginalAttendeesBeforeEditForMultiCalendar } from "../../../hooks/multicalendar/event-card-edit/useSetOriginalAttendeesBeforeEdit";
import { getFlexibilityRangeFromString } from "../../../utils/getFlexibilityRangeFromString";
import {
  getRecurrenceToSave,
  shouldUpdateThisAndFutureEvents,
} from "../../../utils/getRecurrenceToSave";
import { ModifyDiffDetails } from "../../../utils/types";
import { ChangesNotifier } from "../../ChangesNotifier";

export type ModifyDiffEventCardExpandedProps = {
  diffEventDetails: ModifyDiffDetails;
  loading: boolean;
  onClose: () => void;
  onSubmit: (input: SaveModifyEventDiffDetailsInput) => void;
  onCancel: (input: CancelEventDiffDetailsInput, isPermissionAll: boolean) => void;
};

// Temporary constant to show save button
const IS_SENDING_TOO = false;

export const ModifyDiffEventCardExpanded = ({
  diffEventDetails,
  loading,
  onClose,
  onSubmit,
  onCancel,
}: ModifyDiffEventCardExpandedProps) => {
  const {
    id: diffId,
    title: fetchedTitle,
    description: fetchedDescription,
    updatedTime: fetchedTime,
    rsvp: fetchedRsvp,
    currentTime,
    descriptionOmitted: fetchedDescriptionOmitted,
    attendeesOmitted: fetchedAttendeesOmitted,
    eventPermissions: fetchedEventPermissions,
    actionType,
    recurrence: fetchedRecurrence,
    videoConferencingType: fetchedVCType,
    flexDetails: fetchedFlexDetails,
    location: fetchedLocation,
  } = diffEventDetails;

  const defaultExpandedTradeoff = useReadDefaultExpandedTradeoff();
  const { conferencingTypes } = useUserConfrencingTypes();
  const { domains: orgDomains } = useOrgDomains();
  const { workingHours } = useWorkingHours();
  const { startTime, endTime } = useReadEventTimeDetails();
  const { attendees: attendeePeople, attendeesChanged } = useEventAttendees();
  const zone = getRenderTimeZone();
  const ecosystem = useEcosystem();

  const currentTimeInterval = Interval.fromISO(currentTime, { zone });
  const hasDateTimeChanged = fetchedTime && fetchedTime !== currentTime;

  const customRecurrenceString = formatRecurrenceToString(fetchedRecurrence);

  const [viewMode, setViewMode] = useState<EventCardMode>(EventCardMode.default);
  const [updatesPresent, setUpdatesPresent] = useState<boolean>(false);
  const [eventRecurrence, setEventRecurrence] = useState<SelectableRecurrenceOption | string>(
    customRecurrenceString,
  );
  const [flexDetails, setFlexDetails] = useState<EventCardFlex>(
    fetchedFlexDetails || EMPTY_FLEX_DETAILS,
  );
  const [videoType, setVideoType] = useState<ConferencingType | undefined>(
    fetchedVCType || undefined,
  );
  const [title, setTitle] = useState<string>(fetchedTitle);
  const [rsvp, setRsvp] = useState<ResponseStatusEnum>(fetchedRsvp);
  const [description, setDescription] = useState<string>(fetchedDescription ?? "");
  const [location, setLocation] = useState<string>(fetchedLocation ?? "");

  useEffect(() => {
    if (fetchedDescription) {
      setDescription(fetchedDescription);
    }
    if (fetchedVCType) {
      setVideoType(fetchedVCType);
    }
    if (fetchedLocation) {
      setLocation(fetchedLocation);
    }
    setTitle(fetchedTitle);
    setFlexDetails(fetchedFlexDetails);
  }, [diffId]);

  const isWaiting = isWaitingOnAsync(loading);
  const readOnly = !canModifyWholeEvent(fetchedEventPermissions, isWaiting);
  const inCancelledState = actionType === "CANCEL";
  const eventIsRecurring = eventRecurrence !== "None";

  const warningMessage = getEventCardWarning(orgDomains, attendeePeople, fetchedEventPermissions);

  const onFlexChange = (flexible: boolean) => {
    setFlexDetails({
      ...flexDetails,
      isFlexible: flexible,
    });
    setTitle(formatSparkleInTitle(title, flexible));
  };

  const updateForm = (updateState: () => unknown) => {
    updateState();
    setUpdatesPresent(true);
  };

  const attendees = attendeePeople.map((attendee) => attendee.primaryCalendar);
  const attendeesList = attendeePeople.map((attendee) => {
    return { calendarId: attendee.primaryCalendar, optional: !!attendee.isOptional };
  });

  const disabledRecurrenceSaveOptions = getDisabledSaveOptions({
    hasFlexBeenModified: !isEqual(flexDetails, fetchedFlexDetails),
    hasRecurrenceBeenModified: customRecurrenceString !== eventRecurrence,
    isDescriptionOmitted: fetchedDescriptionOmitted,
  });

  const handleSubmit = (saveOption: RepeatingEventSaveOption) => {
    const input: SaveModifyEventDiffDetailsInput = {
      title: title.trim(),
      description: description?.trim(),
      time: Interval.fromISO(`${startTime}/${endTime}`, { zone }).toISO(),
      isFlexible: flexDetails.isFlexible,
      attendees,
      rsvp,
      diffId,
      conferenceType: videoType,
      recurrence: getRecurrenceToSave(eventRecurrence, fetchedRecurrence),
      repeatingEventSaveOption: shouldUpdateThisAndFutureEvents(
        saveOption,
        fetchedRecurrence,
        eventRecurrence,
      ),
      flexDetails: {
        ...flexDetails,
        timeRangeFlexibility: {
          range: getFlexibilityRangeFromString(flexDetails.timeRangeFlexibility.range),
          allowedDays: flexDetails.timeRangeFlexibility.daysOfWeekFlexibility.map(
            convertStringToDayOfWeek,
          ),
        },
      },
      location: location.trim(),
      attendeesList,
    };
    onSubmit(input);
    setUpdatesPresent(false);
  };

  const onToggleCancel = (cancelOption?: RepeatingEventSaveOption) => {
    onCancel(
      { diffId, shouldCancel: !inCancelledState, repeatingEventSaveOption: cancelOption },
      fetchedEventPermissions === EventPermissions.ALL,
    );
  };

  useSetOriginalAttendeesBeforeEditForMultiCalendar({
    diffId,
    calendarIds: attendees,
  });

  const [resetEditsToAttendeesForMultiCalendar] = useResetEditsToAttendeesForMultiCalendar({
    diffId,
  });

  const wrappedOnClose = () => {
    resetEditsToAttendeesForMultiCalendar();

    onClose();
  };

  // Will be adding more to this - Sam 1/29
  const changesMap = {
    startTime:
      !loading &&
      !startTime.equals(
        Interval.fromISO(fetchedTime ?? currentTime, { zone: getRenderTimeZone() }).start,
      ),
    endTime:
      !loading &&
      !endTime.equals(
        Interval.fromISO(fetchedTime ?? currentTime, { zone: getRenderTimeZone() }).end,
      ),
    attendees: attendeesChanged,
  };
  const changesArray = Object.values(changesMap);

  const organizer = attendeePeople.find((a) => a.isOrganizer);

  return (
    <EventCardExpanded
      loading={isWaiting}
      alsoSendUpdates={IS_SENDING_TOO}
      onClose={wrappedOnClose}
      mode={viewMode}
      shouldShowRSVP={canUserRSVP(
        rsvp,
        fetchedEventPermissions,
        ecosystem,
        (organizer && organizer.isYou) || false,
        isWaiting,
      )}
      rsvpModule={
        <ECResponseStatus
          loading={loading}
          rsvpStatus={rsvp}
          onRSVPChange={(rsvpstatus) => updateForm(() => setRsvp(rsvpstatus))}
        />
      }
      footer={
        <ECFooter
          disabled={isWaiting}
          onUpdateCancel={onToggleCancel}
          onSubmit={handleSubmit}
          updatesPresent={changesArray.some((value) => Boolean(value)) || updatesPresent}
          isCancelled={inCancelledState}
          permissions={fetchedEventPermissions}
          disabledRecurrenceSaveOptions={disabledRecurrenceSaveOptions}
          showDropdownOnSave={eventIsRecurring}
          submissionCopy={getSubmitButtonCopy(true)}
          deletionCopy={getDeleteEventCopy(fetchedEventPermissions === EventPermissions.ALL)}
        />
      }
      flexSettingsView={
        !loading &&
        !readOnly && (
          <ECFlexSettings
            isOpen={viewMode === EventCardMode.flexSettings}
            goBack={() => {
              setViewMode(EventCardMode.default);
            }}
            flexDetails={flexDetails}
            flexStatus={null}
            eventName={title}
            onFlexDetailsChange={(flexDetails) =>
              updateForm(() => {
                setFlexDetails(flexDetails);
              })
            }
            availableWorkDays={ALL_WORK_DAYS_SELECTED}
            showMeetingHoursConflict={false}
            minMaxRecommendedValues={getEventMinMaxConstraints(
              undefined,
              workingHours || undefined,
              startTime,
            )}
            onUnpauseEvent={() => {
              toast.error("Not yet implemented");
            }}
          />
        )
      }
    >
      {inCancelledState && (
        <div className="cw-absolute cw-bg-default cw-opacity-50 cw-z-[5] cw-inset-0" />
      )}
      {warningMessage && !isWaiting && !inCancelledState && (
        <WarningMessage type={warningMessage} />
      )}
      <ECTitle
        readOnly={readOnly}
        eventName={title}
        onEventNameChange={(e) => updateForm(() => setTitle(e.target.value))}
      />
      {inCancelledState && (
        <ChangesNotifier
          displayText={
            <>
              <span className="cw-font-semibold">Heads up! </span>
              You're seeing proposed changes that will apply after you confirm the proposal.
            </>
          }
        />
      )}
      <div className="cw-flex cw-flex-col cw-gap-1">
        <ECFlexibility
          flexibilityDescription={""}
          flexibilityStatus={"Default"}
          isFlexible={flexDetails.isFlexible}
          onFlexibilityChange={(flexible) => updateForm(() => onFlexChange(flexible))}
          onPauseChange={(flexible) => updateForm(() => onFlexChange(flexible))}
          readOnly={readOnly}
          goToFlexDetails={() => {
            setViewMode(EventCardMode.flexSettings);
          }}
        />
        <Divider />
        <ECAttendeeWrapper
          nonEditableAttendeeIds={getListOfNonRemovableAttendeeIds(
            fetchedEventPermissions,
            attendeePeople,
          )}
          attendeesOmitted={fetchedAttendeesOmitted}
          readOnly={!canUserInviteAttendees(fetchedEventPermissions, isWaiting)}
          diffId={diffId}
        />
        <Divider />
        <TimeTradeoffs
          readOnly={readOnly}
          defaultExpandedTradeoff={defaultExpandedTradeoff}
          originalInterval={currentTimeInterval}
          hasDateTimeChanged={Boolean(hasDateTimeChanged)}
        />
        <Recurrence
          loading={loading}
          readOnly={readOnly}
          fetchedRecurrenceString={customRecurrenceString}
          selectedOption={eventRecurrence}
          onSelect={(option) => updateForm(() => setEventRecurrence(option))}
        />
        <Divider />
        <ECVideoConf
          onSelect={(videoType) => updateForm(() => setVideoType(videoType))}
          readonly={readOnly}
          selected={videoType}
          enabledTypes={conferencingTypes}
          loading={loading}
        />
        <ECRoomAndLocation
          location={location}
          onLocationChange={(location) => updateForm(() => setLocation(location || ""))}
          onRoomSelect={noop}
          readonly={readOnly}
          rooms={undefined}
          loading={loading}
        />
        <Divider />
        <ECAgenda
          readOnly={readOnly}
          agenda={description ?? ""}
          descriptionOmitted={fetchedDescriptionOmitted}
          onChangeAgenda={(agenda, userModified) => {
            setDescription(agenda);
            if (userModified) {
              setUpdatesPresent(true);
            }
          }}
        />
      </div>
    </EventCardExpanded>
  );
};
